import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";
import { FilmIcon, PlayIcon } from "lucide-react";

import { cn } from "@/shared/lib";

import { useHighlightsAllowed } from "../../api/useHighlightsAllowed";
import { useHighlightsOverview } from "../../api/useHighlightsOverview";
import { isClipHopeless, isClipReady } from "../../model/clipState";

const CLIPS = msg({
  message: "{count, plural, one {# clip} other {# clips}}",
});
const MOMENTS = msg({
  message: "{count, plural, one {# moment} other {# moments}}",
});
const HINT = msg`Open this match in Highlights`;

type MatchHighlightsBadgeProps = {
  matchId: number;
};

// Значок в строке списка матчей (доска 18ж): у матча есть хайлайты — сколько готовых клипов
// (закрашен) или моментов без клипа (контур), ведёт в раздел сразу к этому матчу. Данные — из
// того же ответа раздела, что полоса на главной; матча там нет или хайлайты закрыты — значка нет.
export const MatchHighlightsBadge = ({ matchId }: MatchHighlightsBadgeProps) => {
  const { i18n } = useLingui();
  const allowed = useHighlightsAllowed();
  const overview = useHighlightsOverview();
  if (!allowed) {
    return null;
  }
  const items = (
    overview.data?.matches.find((match) => match.match_id === matchId)?.items ?? []
  ).filter((highlight) => !isClipHopeless(highlight));
  if (items.length === 0) {
    return null;
  }
  const ready = items.filter(isClipReady).length;
  const Icon = ready > 0 ? PlayIcon : FilmIcon;
  return (
    <Link
      to="/highlights"
      search={{ match: matchId }}
      title={i18n._(HINT)}
      className={cn(
        "inline-flex items-center gap-1 rounded-full py-px pr-2 pl-1.5 text-[11px] font-bold no-underline",
        ready > 0
          ? "bg-accent text-accent-foreground hover:bg-accent-strong"
          : "border border-border-strong text-accent hover:border-accent",
      )}
    >
      <Icon aria-hidden="true" className={cn("size-3", ready > 0 && "fill-current")} />
      {i18n._({
        ...(ready > 0 ? CLIPS : MOMENTS),
        values: { count: ready > 0 ? ready : items.length },
      })}
    </Link>
  );
};
